import { ChoixNoyaux } from '@/components/noyaux/choix-noyaux';
import { Button, LoadingRing } from '@pms-digital/design-system';
import { MESSAGE_TEXTE_TROP_LONG, type NoyauCode } from '@rosetta/shared';

// pms-design-exception: the footer starts at the edge of the 56 px icon bar of the AppShell
const GAUCHE_BARRE = 'xl:left-14';

interface PiedSaisieProps {
  noyaux: readonly NoyauCode[];
  onNoyauxChange: (noyaux: NoyauCode[]) => void;
  /** The text is empty (or only blanks): "traduire" stays off. */
  texteVide: boolean;
  /** The text is longer than 10 000 characters (#13): "traduire" stays off. */
  texteTropLong?: boolean;
  /** A translation is being launched (the few calls before its waiting screen opens): everything is locked. */
  enCours: boolean;
  onTraduire: () => void;
}

/**
 * The message that says why "traduire" is off; the text comes before the noyaux, and a text too
 * long (#13) before them too: it is what blocks.
 */
export function aideSaisie(
  texteVide: boolean,
  noyaux: readonly NoyauCode[],
  texteTropLong = false,
): string | null {
  if (texteVide) return 'Veuillez saisir ou charger un texte';
  if (texteTropLong) return MESSAGE_TEXTE_TROP_LONG;
  if (noyaux.length === 0) return 'Veuillez choisir au moins un noyau cible';
  return null;
}

/**
 * The footer of the input screen, pinned to the bottom of the window next to the
 * navbar and without a net (from 1280 px of width: below that, which is what a 1440 px screen
 * zoomed to 200 % gives, it wraps and goes at the end of the page, in the flow, so that
 * "traduire" never leaves the screen, WCAG 1.4.4 and 1.4.10): "Traduire vers" and the noyaux on the left, the help
 * message then "traduire" on the right. While a translation is launched, the button turns
 * into the waiting state and is announced politely (the screen then opens the waiting screen
 * of the translation, W1, #7: the input is not locked while it runs).
 *
 * It is the same button in both states, and the waiting one is `aria-disabled` rather than
 * `disabled` (S14, #5): a button that is replaced, or disabled, while it has the focus drops the
 * keyboard on the page, and the user who pressed Enter on it would have to find their place
 * again once the translation fails. Pressed while waiting, it does nothing.
 */
export function PiedSaisie({
  noyaux,
  onNoyauxChange,
  texteVide,
  texteTropLong = false,
  enCours,
  onTraduire,
}: PiedSaisieProps) {
  const aide = aideSaisie(texteVide, noyaux, texteTropLong);

  return (
    <footer
      className={`z-10 mt-auto flex flex-wrap items-center justify-between gap-x-6 gap-y-3 bg-white px-8 py-3 xl:fixed xl:right-0 xl:bottom-0 ${GAUCHE_BARRE} xl:mt-0 xl:h-20 xl:flex-nowrap xl:py-0`}
    >
      <ChoixNoyaux valeur={noyaux} onChange={onNoyauxChange} verrouille={enCours} />
      {/* The live region stays in the page, so the waiting state is announced when it appears. */}
      <div aria-live="polite" className="flex items-center gap-4">
        {aide === null || enCours ? null : (
          <span className="font-normal text-gray-600 text-small leading-normal">{aide}</span>
        )}
        <Button
          variant="secondary"
          disabled={aide !== null && !enCours}
          aria-disabled={enCours}
          aria-busy={enCours}
          onClick={enCours ? undefined : onTraduire}
        >
          {enCours ? (
            <>
              <LoadingRing size="sm" />
              traduction en cours...
            </>
          ) : (
            'traduire'
          )}
        </Button>
      </div>
    </footer>
  );
}
